/** A date a proposed recurring reservation can't be booked on, from core's `recurring_by_reservation` */
export interface RecurringClash {
  date: string;
  reason: string;
  alternative_resource_ids: string[];
}

/** The dates of a proposed recurring reservation (YYYY-MM-DD, office time zone) and its clashes */
export interface RecurringOccurrences {
  dates: string[];
  clashes: RecurringClash[];
  /** The series goes on past the dates that were checked */
  has_more: boolean;
}

/** Per clashing date: another resource to book that day, or null to skip it */
export type RecurringClashChoices = Record<string, string | null>;

export interface ResolvedRecurringReservation {
  /** Dates the series keeps; empty when none is left */
  dates: string[];
  /** Single reservations on clashing dates, with the resource chosen for them */
  singles: { date: string; resourceId: string }[];
}

const DATE_LIST_PREFIX = "RDATE;VALUE=DATE:";

export const clashReasonLabel = (reason: string, resourceName: string) => {
  switch (reason) {
    case "overlaps_with_schedule":
    case "overlaps_with_recurring_task":
    case "resource_over_capacity":
    case "buffer_overlaps_with_schedule":
      return `${resourceName} is taken`;
    case "office_not_available":
      return "The office is closed";
    case "office_future_limit":
      return "Too far ahead to book";
    case "concurrent_not_allowed":
    case "concurrent_not_allowed_cross_office":
      return "You already have a reservation";
    case "resource_unavailable":
      return `${resourceName} is unavailable`;
    case "resource_assignment_not_allowed":
      return `${resourceName} is assigned to someone else`;
    default:
      return `${resourceName} can't be booked`;
  }
};

/** The dates the series keeps, without the clashing ones, and the clashing dates booked on another resource */
export const resolveRecurringClashes = (
  occurrences: RecurringOccurrences,
  choices: RecurringClashChoices
): ResolvedRecurringReservation => {
  const clashDates = new Set(occurrences.clashes.map((clash) => clash.date));
  return {
    dates: occurrences.dates.filter((date) => !clashDates.has(date)),
    singles: occurrences.clashes.flatMap((clash) => {
      const resourceId = choices[clash.date];
      return resourceId ? [{ date: clash.date, resourceId }] : [];
    })
  };
};

/** A recurrence of exact dates as the API reads it, e.g. RDATE;VALUE=DATE:20261005,20261012 */
export const dateListRule = (dates: string[]) =>
  DATE_LIST_PREFIX + dates.map((date) => date.replaceAll("-", "")).join(",");

export const isDateListRule = (rule: string) => rule.startsWith("RDATE");

/** The first and last dates (YYYY-MM-DD) of a date list rule */
export const dateListRange = (rule: string) => {
  const dates = rule
    .slice(rule.indexOf(":") + 1)
    .split(",")
    .map((value) => value.trim())
    .filter((value) => /^\d{8}$/.test(value))
    .map(
      (value) =>
        `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`
    )
    .sort();
  if (dates.length === 0) return null;
  return { first: dates[0], last: dates[dates.length - 1] };
};

/** A local date-time string (2026-10-05T09:00:00) moved to another date, keeping its time */
export const atDate = (dateTime: string, date: string) =>
  `${date}${dateTime.slice(dateTime.indexOf("T"))}`;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The end of a local start/end range moved to another start date, keeping how many days later it ends (overnight bookings) */
export const endAtDate = (start: string, end: string, date: string) => {
  const days = Math.round(
    (Date.parse(end.slice(0, 10)) - Date.parse(start.slice(0, 10))) / DAY_MS
  );
  const endDate = new Date(Date.parse(date) + days * DAY_MS)
    .toISOString()
    .slice(0, 10);
  return atDate(end, endDate);
};

/** One reservation made to book a proposed reservation, possibly recurring, with its clashing dates resolved */
export interface PlannedBooking {
  /**
   * Stable across Reserve attempts: "series" (the reservation itself, as proposed or without its clashing
   * dates), "date:<day>" (the only date it keeps) or "single:<day>" (a clashing date on another resource)
   */
  key: string;
  resourceId: string;
  /** Local wall clock, e.g. 2026-10-05T09:00:00 */
  start: string;
  end: string;
  /** "original": the proposed recurrence, if any; "dates": the kept dates as exact dates; "none": a single reservation */
  recurrence: "original" | "dates" | "none";
  /** The exact dates of a "dates" recurrence, or the date of a single reservation */
  dates: string[];
  /** A clashing date booked on another resource */
  moved: boolean;
}

/** Schedule IDs made so far, by booking key */
export type BookedSchedules = Record<string, string[]>;

/**
 * The reservations that book a proposed reservation: as proposed when nothing clashes, otherwise the series
 * without its clashing dates (as exact dates, or the one date left) and each clashing date the user moved to
 * another resource. Every booking is independent, so one failing doesn't stop the others.
 */
export const planRecurringBookings = (
  proposed: { resourceId: string; start: string; end: string },
  occurrences: RecurringOccurrences | undefined,
  choices: RecurringClashChoices
): PlannedBooking[] => {
  if (!occurrences || occurrences.clashes.length === 0) {
    return [
      {
        key: "series",
        resourceId: proposed.resourceId,
        start: proposed.start,
        end: proposed.end,
        recurrence: "original",
        dates: [],
        moved: false
      }
    ];
  }

  const { dates, singles } = resolveRecurringClashes(occurrences, choices);
  const onDate = (resourceId: string, date: string) => ({
    resourceId,
    start: atDate(proposed.start, date),
    end: endAtDate(proposed.start, proposed.end, date)
  });
  const kept: PlannedBooking[] =
    dates.length > 1
      ? [
          {
            key: "series",
            ...onDate(proposed.resourceId, dates[0]),
            recurrence: "dates",
            dates,
            moved: false
          }
        ]
      : dates.map((date) => ({
          key: `date:${date}`,
          ...onDate(proposed.resourceId, date),
          recurrence: "none",
          dates: [date],
          moved: false
        }));

  return [
    ...kept,
    ...singles.map(({ date, resourceId }) => ({
      key: `single:${date}`,
      ...onDate(resourceId, date),
      recurrence: "none" as const,
      dates: [date],
      moved: true
    }))
  ];
};

/** What is still to book, and whether every booking of the plan is made */
export const bookingProgress = (
  booked: BookedSchedules,
  plan: PlannedBooking[]
) => {
  const missing = plan.filter((booking) => !booked[booking.key]?.length);
  return {
    missing,
    complete: plan.length > 0 && missing.length === 0,
    scheduleIds: plan.flatMap((booking) => booked[booking.key] ?? [])
  };
};

/** Clashing dates already booked on another resource; their choice can't change any more */
export const lockedClashDates = (booked: BookedSchedules) =>
  Object.keys(booked)
    .filter((key) => key.startsWith("single:") && booked[key]?.length)
    .map((key) => key.slice("single:".length));

/**
 * The reservation a workplace request goes with: the series (or the one date it keeps), or the first
 * booking when every date was moved to another resource. Undefined until that booking is made.
 */
export const requestScheduleId = (
  booked: BookedSchedules,
  plan: PlannedBooking[]
) => {
  const primary = plan.find((booking) => !booking.moved) ?? plan[0];
  return primary ? booked[primary.key]?.[0] : undefined;
};

/** A reservation a card made: the series, a date it keeps, or a clashing date moved to another resource */
export interface MadeBooking {
  key: string;
  scheduleId: string;
  resourceId: string;
  /** A series of dates, cancelled as a whole with `recurring` */
  recurring: boolean;
  /** Its (first) date, YYYY-MM-DD */
  date: string;
}

/** Everything a card booked for a plan, in the plan's order */
export const madeBookings = (
  booked: BookedSchedules,
  plan: PlannedBooking[],
  proposalIsRecurring: boolean
): MadeBooking[] =>
  plan.flatMap((booking) => {
    const scheduleId = booked[booking.key]?.[0];
    if (!scheduleId) return [];
    return [
      {
        key: booking.key,
        scheduleId,
        resourceId: booking.resourceId,
        recurring:
          booking.recurrence === "dates" ||
          (booking.recurrence === "original" && proposalIsRecurring),
        date: booking.dates[0] ?? booking.start.slice(0, 10)
      }
    ];
  });

/**
 * What a cancel cancels. All: the series as a whole and every date booked on its own. Otherwise one date: the
 * card's own date where it was booked on another resource, else the series' first date (the series stays
 * booked, `wholeBooking` false), which isn't the card's date when that one was skipped. `date` is the date
 * cancelled.
 */
export const cancelTargets = (
  bookings: MadeBooking[],
  all: boolean,
  ownDate: string
): {
  key: string;
  scheduleId: string;
  recurring: boolean;
  wholeBooking: boolean;
  date: string;
}[] => {
  if (all) {
    return bookings.map(({ key, scheduleId, recurring, date }) => ({
      key,
      scheduleId,
      recurring,
      wholeBooking: true,
      date
    }));
  }
  const own =
    bookings.find(
      (booking) => booking.key.startsWith("single:") && booking.date === ownDate
    ) ??
    bookings.find((booking) => !booking.key.startsWith("single:")) ??
    [...bookings].sort((a, b) => a.date.localeCompare(b.date))[0];
  return own
    ? [
        {
          key: own.key,
          scheduleId: own.scheduleId,
          recurring: false,
          wholeBooking: !own.recurring,
          date: own.date
        }
      ]
    : [];
};

/** The reservation to check in to today: a date booked on its own for today, else the series */
export const checkInTarget = (bookings: MadeBooking[], today: string) =>
  bookings.find(
    (booking) => booking.key.startsWith("single:") && booking.date === today
  ) ??
  bookings.find((booking) => !booking.key.startsWith("single:")) ??
  bookings[0];

/**
 * The last date booked when the series goes on past the dates that were checked and some of them clash: the
 * series is then booked as its checked dates only. Null when booking covers the whole series.
 */
export const bookedThrough = (occurrences: RecurringOccurrences | undefined) =>
  occurrences?.has_more && occurrences.clashes.length > 0
    ? (occurrences.dates[occurrences.dates.length - 1] ?? null)
    : null;
